<template>
  <application-layout>
    <v-container>
      <v-layout v-if="infoLoading" justify-center>
        <v-progress-circular :size="70" :width="7" indeterminate class="mb-3" />
      </v-layout>
      <v-layout v-else column>
        <v-layout align-center justify-space-between class="mb-3">
          <v-flex>
            <h1>{{ facility.name }} ({{ facility.location_code }})</h1>
            <h2>Local time: {{ currentTime }}</h2>
          </v-flex>
          <v-layout justify-end>
            <request-uhaul />
            <facility-tenant-search :toTenantView="toTenantView">
              <v-btn
                data-testid="find-location-tenant-btn"
                class="find-location-tenant-btn success px-3"
              >
                <v-icon class="mr-3">search</v-icon>
                <span>Find {{ facility.location_code }} Tenant</span>
              </v-btn>
            </facility-tenant-search>
            <v-btn
              data-testid="refresh-reservations-btn"
              class="refresh-reservations-btn ml-4 indigo white--text"
              @click="refreshReservations"
              :loading="refreshLoading"
              outlined
            >
              <v-icon class="mr-3">refresh</v-icon>
              Refresh Reservations
            </v-btn>
          </v-layout>
        </v-layout>
        <v-tabs v-model="tab" class="mb-6">
          <v-tab
            data-testid="facility-location-details-tab"
            class="facility-location-details-tab"
          >
            {{ facility.location_code }} Details
          </v-tab>
          <v-tab
            data-testid="facility-location-units-tab"
            class="facility-location-units-tab"
          >
            {{ facility.location_code }} Units
          </v-tab>
          <v-tab
            data-testid="facility-location-reservations-tab"
            class="facility-location-reservations-tab"
            @click="getFacilityReservations"
          >
            {{ facility.location_code }} Reservations
          </v-tab></v-tabs
        >
        <v-tabs-items v-model="tab" style="background-color: transparent">
          <v-tab-item eager>
            <facility-details :facility="facility" />
          </v-tab-item>
          <v-tab-item eager>
            <facility-units
              :availableUnits="availableUnits"
              @reservationCreated="getFacilityReservations"
            />
          </v-tab-item>
          <v-tab-item eager>
            <facility-reservations
              :facility="facility"
              @reservationUpdated="getFacilityReservations"
              @tenantUpdated="getFacilityReservations"
              @leadConverted="getFacilityReservations"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-layout>
    </v-container>
  </application-layout>
</template>

<script>
/* eslint-disable eqeqeq */
import ApplicationLayout from "@/layouts/ApplicationLayout";
import { EventBus } from "@/Event";
import FacilityDetails from "@/components/FacilityDetails";
import FacilityReservations from "@/views/facilities/partials/FacilityReservations";
import FacilityTenantSearch from "@/views/facilities/partials/FacilityTenantSearch";
import FacilityUnits from "@/views/facilities/partials/FacilityUnits";
import RequestUhaul from "@/components/RequestUhaul";
import { mapState } from "vuex";
import moment from "moment-timezone";
import timezones from "../../helpers/time-zones.js";

export default {
  data() {
    return {
      infoLoading: false,

      facility: [],
      availableUnits: [],

      facilityUpdate: null,

      toTenantView: true,
      hasRefreshed: false,
      eventFired: false,

      currentTime: undefined,
      timeInterval: 0,
      timezones: timezones,

      tab: 0
    };
  },

  methods: {
    getFacilityInfo() {
      this.infoLoading = true;
      this.$store
        .dispatch("getFacility", this.facilityId)
        .then((resp) => {
          let facility = resp.data.facility;
          this.$route.params.facilityId = resp.data.facility.id;
          this.facility = facility;
          this.$store.commit("commitAlerts", facility.alerts);
          this.availableUnits = facility.units.parking
            ? facility.units.storage.concat(facility.units.parking)
            : facility.units.storage;
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
        })
        .finally(() => {
          this.infoLoading = false;
          this.$store.commit("setReservationsLoading", false);
        });
    },

    getFacilityAlerts() {
      let oldAlerts = this.$store.state.facilities.facilityAlerts;
      this.$store
        .dispatch("getFacilityAlerts", this.facilityId)
        .then((resp) => {
          if (
            !oldAlerts.length ||
            JSON.stringify(oldAlerts) == JSON.stringify(resp.data.alerts)
          ) {
            // do nothing, no new alerts have been added
          } else {
            this.$store.dispatch(
              "handleAlert",
              "A new alert was recently added for this facility. Please view the alert panel for details."
            );
          }
          this.$store.commit("commitAlerts", resp.data.alerts);
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
        });
    },

    getFacilityReservations() {
      let payload = {
        facilityId: this.facilityId
      };

      this.$store.dispatch("getReservations", payload);
    },

    async refreshReservations() {
      await this.getFacilityReservations();
      localStorage.setItem(
        "refreshedFacilities",
        this.refreshedFacilities
          ? this.refreshedFacilities + this.facility.location_code
          : this.facility.location_code
      );
      this.hasRefreshed = true;
    },

    getTime() {
      this.currentTime = moment().tz(this.facilityTimezone).format("LTS");
    },

    getMoveInCost() {
      if (this.$store.getters.unitId) {
        const payload = {
          facilityId: this.$route.params.facilityId
        };
        this.$store.dispatch("getMoveInCost", payload);
      }
    }
  },

  watch: {
    "$store.state.tenant.options": {
      handler() {
        this.getMoveInCost();
      },
      deep: true
    },
    "$store.state.reservations.invalidPromotion"(newState, oldState) {
      // if we detect an invalid promotion tied to a reservation,
      // we need to fetch the available promotions for the facility
      if (oldState !== false || newState !== true) return;
      if (!this.facility || !this.facility.id) return;
      this.$store.dispatch("getFacilityPromotions", {
        facilityId: this.facility.id
      });
    },
    facilityId() {
      this.getFacilityInfo();
    }
  },

  computed: {
    ...mapState({
      options: (state) => state.tenant.options,
      reservationsLoading: (state) => state.reservations.reservationsLoading,
      reservations: (state) => state.reservations.reservations
    }),
    refreshedFacilities() {
      return localStorage.getItem("refreshedFacilities");
    },

    refreshLoading() {
      return !!(this.reservations.length && this.reservationsLoading);
    },

    facilityAlreadyWaived() {
      const refreshedFacilities = localStorage.getItem("refreshedFacilities");
      return (
        refreshedFacilities &&
        refreshedFacilities.includes(this.facility.location_code)
      );
    },

    facilityTimezone() {
      if (this.facility && this.facility.time_zone) {
        return this.facility.time_zone;
      } else {
        return "America/New_York";
      }
    },

    facilityId() {
      return this.$route.params.facilityId;
    }
  },

  created() {
    EventBus.$on("reloadCurrentFacility", () => {
      this.eventFired = true;
      this.getFacilityInfo();
    });

    if (!this.eventFired) {
      this.getFacilityInfo();
    }

    this.facilityUpdate = setInterval(() => {
      if (
        this.$store.state.facilities.facility &&
        this.$store.state.facilities.facility.id
      ) {
        return;
      }
      this.getFacilityAlerts();
      if (this.reservations.length) {
        this.getFacilityReservations();
      }
    }, 900000); // check for new alerts & reservations every 15 min
  },

  mounted() {
    this.timeInterval = setInterval(() => {
      this.getTime();
    }, 1000);
    this.$store.dispatch("getFacilityPromotions", {
      facilityId: this.facilityId,
      realtime: true
    });
  },

  beforeDestroy() {
    clearInterval(this.facilityUpdate);
    clearInterval(this.timeInterval);
  },

  components: {
    ApplicationLayout,
    FacilityDetails,
    FacilityReservations,
    FacilityUnits,
    RequestUhaul,
    // SendText,
    FacilityTenantSearch
  }
};
</script>
